import { getMerchant } from 'actions/merchant'
import { getCurrentMerchantUser } from 'actions/merchantUser'
import { legalDocsApi } from 'api/legalDocs'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useCurrentMerchantUser from 'hooks/useCurrentMerchantUser'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import useIsThanxAdmin from 'hooks/useIsThanxAdmin'
import useLegalDocsBlock from 'hooks/useLegalDocsBlock'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  legalDocsCleared,
  selectLegalDocsEnforced,
} from 'slices/legalDocsBlock'

const blockDismissedKey = (id: number) => `legal_docs_block_dismissed_${id}`
const PAGE_LOAD_TOKEN = String(Date.now())

type UseLegalDocsBlockGuardResult = {
  showModal: boolean
  handleContinue: () => void
}

type AuthSlice = { auth: { current_merchant_id: number | null } }

export default function useLegalDocsBlockGuard(): UseLegalDocsBlockGuardResult {
  const dispatch = useDispatch()
  const flag = useFlag('mx-legal-docs-enforcement', 'off')
  const merchant = useCurrentMerchant()
  const merchantUser = useCurrentMerchantUser()
  const enforced = useSelector(selectLegalDocsEnforced)
  const authMerchantId = useSelector(
    (state: AuthSlice) => state.auth.current_merchant_id
  )
  // Thanx admins impersonate the merchant's admin user, so `is_admin` reads
  // true on the impersonated session. They are not the responsible party for
  // the merchant's legal docs and must keep navigation freedom — otherwise
  // history.block traps them on the impersonated merchant with no way back
  // to /choose.
  const isThanxAdmin = useIsThanxAdmin()
  // Receiving a `legal_docs_required` 403 implies the user is an admin
  // (backend only enforces against admins), so it's a sufficient signal on
  // its own. Falling back to merchant_user covers the happy path where data
  // loads normally.
  const isAdmin = merchantUser?.is_admin || enforced
  // Treat the user as the merchant's responsible admin only when they admin
  // this merchant AND are not a Thanx admin impersonating.
  const isMerchantAdmin = !!isAdmin && !isThanxAdmin
  // Prefer the ORM merchant id; fall back to the auth cookie when the ORM
  // hasn't loaded because GET /merchants/:id was also blocked.
  const merchantId = merchant?.id ?? authMerchantId ?? undefined
  const shouldCheck = flag === 'block' && isMerchantAdmin && !!merchantId

  const { data, isFetching } = legalDocsApi.useGetComplianceStatusQuery(
    merchantId,
    { skip: !shouldCheck }
  )

  const hasFreshComplianceData = !!data && !isFetching
  const dataSaysNonCompliant =
    hasFreshComplianceData && !data.legal_docs_compliant
  const isBlocked =
    flag === 'block' && isMerchantAdmin && (enforced || dataSaysNonCompliant)

  const [showModal, setShowModal] = useState(
    () =>
      !merchantId ||
      sessionStorage.getItem(blockDismissedKey(merchantId)) !== PAGE_LOAD_TOKEN
  )

  useEffect(() => {
    if (!isBlocked || !merchantId) return
    const dismissed =
      sessionStorage.getItem(blockDismissedKey(merchantId)) === PAGE_LOAD_TOKEN
    if (!dismissed) {
      setShowModal(true)
    }
  }, [isBlocked, merchantId])

  // Clear the enforcement flag once compliance status confirms the merchant
  // is compliant — covers the post-publish path where the user resolved the
  // missing docs and the ComplianceStatus tag refetched. Also re-trigger the
  // identity reads (merchant_users/me, /merchants/:id) since they were 403'd
  // during the block; without these, ProtectedRoute would revert to spinner
  // once `enforced` flips back to false.
  useEffect(() => {
    if (enforced && data?.legal_docs_compliant) {
      dispatch(legalDocsCleared())
      dispatch(getCurrentMerchantUser())
      if (merchantId) {
        dispatch(getMerchant(merchantId))
      }
    }
  }, [enforced, data?.legal_docs_compliant, dispatch, merchantId])

  const handleContinue = useCallback(() => {
    if (merchantId) {
      sessionStorage.setItem(blockDismissedKey(merchantId), PAGE_LOAD_TOKEN)
    }
    setShowModal(false)
  }, [merchantId])

  const handleBlockedNavigation = useCallback(() => {
    if (merchantId) {
      sessionStorage.removeItem(blockDismissedKey(merchantId))
    }
    setShowModal(true)
  }, [merchantId])

  const redirectTo = useFlag('mx-unavailable-item-visibility', false)
    ? '/settings/terms_and_policies'
    : '/brand_content#terms_and_policies'

  useLegalDocsBlock(isBlocked, redirectTo, handleBlockedNavigation)

  return { showModal: isBlocked && showModal, handleContinue }
}
